/**
 * 세금계산서 신청 — 기사접수 앱(dytaxdriver) 신청 화면을 대신한다(plan/driver-orders.md ②).
 * 신청자는 로그인한 기사 본인 — 「다른 기사 대신 신청」을 고르면 그 기사(#332 · 넣은 사람은 나로 남는다). 거래처는 목록(또는 최근 거래처)에서만 고른다 —
 * 목록에 없는 거래처는 회계팀이 사업자등록증·이메일을 받아 등록한다(게시판 #132 · #119 기사 직접 등록을 걷어냄). 서버도 사업자번호로 다시 막는다.
 * 금액은 공급가로 넣거나 「부가세 포함」으로 넣는다(포함이면 ÷1.1 원 단위 반올림 — 발행대장과 같은 규칙).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { driverApi, driverError, uploadDriverInvoiceFile, type DriverCustomer, type DriverInvoiceForm } from '../lib/driverApi';
import Screen, { ErrorBanner, Loading, SubmitError } from './Screen';
import ProxyPicker, { proxyName, type ProxyTarget } from './ProxyPicker';
import { won } from './format';

const digits = (s: string) => s.replace(/[^0-9]/g, '');
const NOT_LISTED = '목록에 없는 거래처는 사업자등록증과 발행 이메일을 회계팀에 보내 등록을 요청해 주세요.';
const todayKst = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);

/** 입력 금액 → 공급가·부가세(원). 포함가면 공급가 = 합계÷1.1 반올림, 세액 = 차. 아니면 세액 = 10% 절사. */
function amounts(raw: string, included: boolean): { supply: number; vat: number } | null {
  const n = Number(digits(raw));
  if (!n) return null;
  if (included) { const supply = Math.round(n / 1.1); return { supply, vat: n - supply }; }
  return { supply: n, vat: Math.floor(n / 10) };
}

export default function NewInvoice() {
  const navigate = useNavigate();
  const [form, setForm] = useState<DriverInvoiceForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [corpId, setCorpId] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<DriverCustomer[]>([]);
  const [cust, setCust] = useState({ name: '', biz: '', email: '', phone: '' });
  const [picked, setPicked] = useState(false);
  const [requestDate, setRequestDate] = useState(todayKst());
  const [amount, setAmount] = useState('');
  const [included, setIncluded] = useState(false);
  const [memo, setMemo] = useState('');
  const [attach, setAttach] = useState<{ path: string; type: string; name: string } | null>(null);
  const [dists, setDists] = useState<{ driverId: number; label: string; amount: string }[]>([]);
  const [peerQ, setPeerQ] = useState('');
  const [peers, setPeers] = useState<{ driverId: number; label: string }[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [proxy, setProxy] = useState<ProxyTarget | null>(null);

  // 신청 칸(법인·신청자) — 대신 신청할 기사를 고르면 그 기사 기준으로 다시 불러온다(#332).
  useEffect(() => {
    let live = true;
    setForm(null); setCorpId(null);
    driverApi.invoiceForm(proxy ? { forDriverId: proxy.driverId } : {})
      .then((f) => { if (!live) return; setForm(f); if (f.corps.length === 1) setCorpId(f.corps[0]!.corp_id); })
      .catch((e) => {
        if (!live) return;
        setError(driverError(e, '신청 화면을 열지 못했습니다.'));
        if (proxy) setProxy(null); // 대신 신청이 막히면 내 이름 화면으로 돌아간다
      });
    return () => { live = false; };
  }, [proxy]);

  /** 대신 신청할 기사 바꾸기 — 첨부는 그 기사 칸에 올라가므로 비우고, 그 기사에게 가는 배분은 뺀다. */
  function changeProxy(t: ProxyTarget | null) {
    setError(null); setAttach(null);
    if (t) setDists((xs) => xs.filter((d) => d.driverId !== t.driverId));
    setProxy(t);
  }

  // 치는 대로 찾기(멈추면 한 번)
  useEffect(() => {
    if (picked || q.trim().length < 2) { setFound([]); return; }
    const t = setTimeout(() => { driverApi.searchCustomers({ q: q.trim() }).then((r) => setFound(r.rows)).catch(() => setFound([])); }, 300);
    return () => clearTimeout(t);
  }, [q, picked]);
  useEffect(() => {
    if (peerQ.trim().length < 2) { setPeers([]); return; }
    const t = setTimeout(() => { driverApi.searchPeers({ q: peerQ.trim() }).then((r) => setPeers(r.rows)).catch(() => setPeers([])); }, 300);
    return () => clearTimeout(t);
  }, [peerQ]);

  function pick(c: DriverCustomer) {
    setCust({ name: c.cust_name, biz: c.biz_number ?? '', email: c.email ?? '', phone: c.phone ?? '' });
    setQ(c.cust_name); setPicked(true); setFound([]);
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy('파일 올리는 중…'); setError(null);
    try { setAttach(await uploadDriverInvoiceFile(f, proxy?.driverId)); } catch (err) { setError(driverError(err, '파일을 올리지 못했습니다.')); } finally { setBusy(null); }
  }

  const amt = amounts(amount, included);
  const distSum = dists.reduce((s, d) => s + Number(digits(d.amount) || 0), 0);

  async function submit() {
    setError(null);
    if (!corpId) return setError('법인을 고르세요.');
    if (!picked) return setError(`거래처를 목록에서 고르세요. ${NOT_LISTED}`);
    // #333 번호 없는 거래처(나라장터·서류 건)도 목록에서 골랐으면 신청된다 — 사업자번호 칸은 비었거나 글자일 수 있다(서버가 목록과 대조).
    if (!cust.email.trim()) return setError('발행 이메일을 입력하세요.');
    if (!amt) return setError('금액을 입력하세요.');
    if (distSum > amt.supply) return setError('배분 합계가 공급가보다 큽니다.');
    if (dists.some((d) => !Number(digits(d.amount)))) return setError('배분 금액을 입력하세요.');
    setBusy('신청하는 중…');
    try {
      const r = await driverApi.requestInvoice({
        corpId, custName: cust.name.trim(), bizNumber: cust.biz.trim(), email: cust.email.trim(), phone: cust.phone.trim() || undefined,
        requestDate, supply: String(amt.supply), vat: String(amt.vat), memo: memo.trim() || undefined, attach,
        dists: dists.map((d) => ({ driverId: d.driverId, amount: digits(d.amount) })),
        forDriverId: proxy?.driverId,
      });
      setDone(r.invoiceNo);
    } catch (e) { setError(driverError(e, '신청하지 못했습니다.')); } finally { setBusy(null); }
  }

  if (done) {
    return (
      <Screen title="세금계산서 신청" back="/invoices">
        <div className="driver-card p-6 text-center">
          <p className="text-2xl font-bold text-ink">{proxy ? `${proxyName(proxy)} 기사님 대신 신청했습니다` : '신청했습니다'}</p>
          <p className="mt-3 text-lg text-ink-soft">번호 <b className="text-ink">{done}</b></p>
          <p className="mt-2 text-base text-ink-soft">회계팀이 발행하면 「세금계산서」 목록에 「발행완료」로 바뀝니다.</p>
        </div>
        <button className="driver-btn mt-6" onClick={() => navigate('/invoices')}>목록으로</button>
      </Screen>
    );
  }
  if (!form) return <Screen title="세금계산서 신청" back="/invoices">{error ? <ErrorBanner>{error}</ErrorBanner> : <Loading />}</Screen>;
  if (!form.open) {
    return (
      <Screen title="세금계산서 신청" back="/invoices">
        <p className="rounded-xl bg-canvas px-4 py-8 text-center text-lg text-ink-soft">
          세금계산서 신청은 아직 <b className="text-ink">기사접수 앱</b>에서 받습니다.<br />여기서 받는 날짜는 회사에서 안내합니다.
        </p>
      </Screen>
    );
  }

  return (
    <Screen title="세금계산서 신청" back="/invoices" acting>
      <ProxyPicker value={proxy} onChange={changeProxy} disabled={busy !== null} />
      <div className="space-y-6">
        <Field label="신청자"><p className="text-lg font-semibold text-ink">{form.requester}</p></Field>

        {form.corps.length > 1 && (
          <Field label="법인">
            <div className="grid grid-cols-2 gap-3">
              {form.corps.map((c) => (
                <button key={c.corp_id} className={`min-h-touch rounded-xl border-2 text-lg ${corpId === c.corp_id ? 'border-brand-600 bg-brand-50 font-bold text-brand-700' : 'border-line'}`}
                        onClick={() => setCorpId(c.corp_id)}>{c.short_name}</button>
              ))}
            </div>
          </Field>
        )}

        <Field label="거래처">
          <input className="driver-input" placeholder="거래처 이름 두 글자 이상" value={q}
                 onChange={(e) => { setQ(e.target.value); setPicked(false); setCust((c) => ({ ...c, name: e.target.value })); }} />
          {found.length > 0 && (
            <ul className="driver-card mt-2 divide-y">
              {found.map((c, i) => (
                <li key={i}><button className="min-h-touch w-full px-4 py-2 text-left text-lg" onClick={() => pick(c)}>
                  {c.cust_name}<span className="ml-2 text-base text-ink-soft">{c.biz_number}</span></button></li>
              ))}
            </ul>
          )}
          {!picked && q.trim().length >= 2 && found.length === 0 && <p className="mt-2 text-base text-ink-soft">찾는 거래처가 없습니다. {NOT_LISTED}</p>}
          {form.recent.length > 0 && !picked && q === '' && (
            <div className="mt-3 flex flex-wrap gap-2">
              {form.recent.slice(0, 6).map((c, i) => (
                <button key={i} className="rounded-full border-2 border-line px-3 py-2 text-base" onClick={() => pick(c)}>{c.cust_name}</button>
              ))}
            </div>
          )}
        </Field>
        <Field label="사업자번호(번호 없는 거래처는 비어 있음)"><input className="driver-input" inputMode="numeric" value={cust.biz} onChange={(e) => setCust({ ...cust, biz: e.target.value })} /></Field>
        <Field label="발행 이메일"><input className="driver-input" type="email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} /></Field>
        <Field label="거래처 연락처(선택)"><input className="driver-input" inputMode="tel" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} /></Field>
        <Field label="요청일자"><input className="driver-input" type="date" value={requestDate} onChange={(e) => setRequestDate(e.target.value)} /></Field>

        <Field label="금액">
          <div className="mb-3 grid grid-cols-2 gap-3">
            {[false, true].map((v) => (
              <button key={String(v)} className={`min-h-touch rounded-xl border-2 text-lg ${included === v ? 'border-brand-600 bg-brand-50 font-bold text-brand-700' : 'border-line'}`}
                      onClick={() => setIncluded(v)}>{v ? '부가세 포함' : '공급가(부가세 별도)'}</button>
            ))}
          </div>
          <input className="driver-input" inputMode="numeric" placeholder="원" value={amount ? won(digits(amount)) : ''} onChange={(e) => setAmount(digits(e.target.value))} />
          {amt && <p className="mt-2 text-base text-ink-soft">공급가 {won(amt.supply)} + 부가세 {won(amt.vat)} = 합계 <b className="text-ink">{won(amt.supply + amt.vat)}</b>원</p>}
        </Field>

        <Field label="사업자등록증(선택)">
          {attach ? (
            <p className="flex items-center justify-between gap-3 text-lg"><span className="truncate">📎 {attach.name}</span>
              <button className="text-base text-ink-soft underline" onClick={() => setAttach(null)}>빼기</button></p>
          ) : (
            <div className="relative">
              <div className="flex min-h-touch items-center justify-center rounded-xl border-2 border-dashed border-line text-lg text-ink-soft">사진·PDF 올리기</div>
              <input type="file" accept="image/*,application/pdf" className="absolute inset-0 cursor-pointer opacity-0" aria-label="사업자등록증 올리기" onChange={onFile} disabled={busy !== null} />
            </div>
          )}
        </Field>

        <Field label="내부배분 — 다른 기사에게(선택)">
          {dists.map((d, i) => (
            <div key={d.driverId} className="mb-3 flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-lg">{d.label}</span>
              <input className="driver-input w-36" inputMode="numeric" placeholder="원" value={d.amount ? won(digits(d.amount)) : ''}
                     onChange={(e) => setDists(dists.map((x, j) => (j === i ? { ...x, amount: digits(e.target.value) } : x)))} />
              <button className="min-h-touch px-2 text-2xl text-ink-soft" aria-label="배분 빼기" onClick={() => setDists(dists.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <input className="driver-input" placeholder="기사 이름 또는 차량번호(예: 3899)" value={peerQ} onChange={(e) => setPeerQ(e.target.value)} />
          {peers.length > 0 && (
            <ul className="driver-card mt-2 divide-y">
              {peers.filter((p) => p.driverId !== proxy?.driverId && !dists.some((d) => d.driverId === p.driverId)).map((p) => (
                <li key={p.driverId}><button className="min-h-touch w-full px-4 py-2 text-left text-lg"
                  onClick={() => { setDists([...dists, { driverId: p.driverId, label: p.label, amount: '' }]); setPeerQ(''); setPeers([]); }}>{p.label}</button></li>
              ))}
            </ul>
          )}
          {dists.length > 0 && amt && <p className="mt-2 text-base text-ink-soft">내 몫 {won(amt.supply - distSum)}원(공급가 기준)</p>}
        </Field>

        <Field label="기타 입력사항(선택)"><textarea className="driver-input min-h-[6rem] py-3" value={memo} onChange={(e) => setMemo(e.target.value)} /></Field>

        <SubmitError message={error} />
        <button className="driver-btn" disabled={busy !== null} onClick={() => void submit()}>{busy ?? '신청하기'}</button>
      </div>
    </Screen>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-base font-semibold text-ink-soft">{label}</p>
      {children}
    </div>
  );
}
